
import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
    
    // Safari-specific logging
    console.log('🔍 ErrorBoundary: Component initialized');
    console.log('🔍 Safari User Agent:', navigator.userAgent);
    console.log('🔍 Safari Version:', navigator.userAgent.includes('Safari'));
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    console.error('🚨 ErrorBoundary: Error caught in getDerivedStateFromError:', error);
    console.error('🚨 Error stack:', error.stack);
    console.error('🚨 Error name:', error.name);
    console.error('🚨 Error message:', error.message);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('🚨 ErrorBoundary: Error caught by boundary:', error, errorInfo);
    console.error('🚨 Component stack:', errorInfo.componentStack);
    
    // Safari-specific error logging
    if (navigator.userAgent.includes('Safari')) {
      console.error('🦎 Safari Error Details:', {
        error: error.message,
        stack: error.stack,
        props: errorInfo.componentStack
      });
    }
  }

  render() {
    console.log('🔍 ErrorBoundary render - hasError:', this.state.hasError);
    
    if (this.state.hasError) {
      console.log('🔍 ErrorBoundary: Rendering fallback UI');
      return this.props.fallback || <ErrorFallback error={this.state.error} />;
    }

    console.log('🔍 ErrorBoundary: Rendering children normally');
    return this.props.children;
  }
}

const ErrorFallback: React.FC<{ error?: Error }> = ({ error }) => {
  console.log('🔍 ErrorFallback component rendering');
  
  // Safe language hook usage with fallback
  let t: any;
  try {
    const { t: useT } = useLanguage();
    t = useT;
    console.log('🔍 ErrorFallback: Language context loaded successfully');
  } catch (err) {
    console.error('🚨 ErrorFallback: Language context failed, using fallback');
    t = (key: string, ar: string, en: string) => en; // Fallback to English
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center p-8 max-w-md">
        <h1 className="text-2xl font-bold text-destructive mb-4">
          {t('something_went_wrong', 'حدث خطأ ما', 'Something went wrong')}
        </h1>
        <p className="text-muted-foreground mb-4">
          {t('error_loading_application', 'حدث خطأ أثناء تحميل التطبيق', 'An error occurred while loading the application')}
        </p>
        {error && (
          <details className="text-left bg-muted p-4 rounded mb-4">
            <summary className="cursor-pointer font-semibold">Error Details</summary>
            <pre className="mt-2 text-sm overflow-auto">
              {error.message}
              {error.stack && (
                <>
                  <br />
                  <br />
                  {error.stack}
                </>
              )}
            </pre>
          </details>
        )}
        <button
          onClick={() => {
            console.log('🔄 Reloading page...');
            window.location.reload();
          }}
          className="bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90"
        >
          {t('reload_page', 'إعادة تحميل الصفحة', 'Reload Page')}
        </button>
      </div>
    </div>
  );
};

export default ErrorBoundary;
